// ————————————————————————————————————————————
// Buffered Frame Presenter (Nivoda-Inspired Architecture)
//
// Real-browser validation result: manual drag became very smooth and
// realistic on this presenter; autoplay, at the existing 12fps
// (assets/diamond-viewer.js's AUTOPLAY_FPS), improved but hasn't fully
// closed the gap with the single-<img> src-swapping path this replaced.
// Approved as the production presentation layer.
//
// This is NOT the Frame Engine. It knows nothing about frame indices,
// URLs, top_index, autoplay, drag math, caching, or the network — all of
// that stays exactly where it already lives (assets/diamond-frame-engine.js).
// This file answers exactly one question: once the Viewer already has a
// resolved src to paint, *how* does that pixel reach the screen without
// ever leaving the stage blank?
//
// Why this exists (see the real-Nivoda-DOM audit that motivated it): the
// live Nivoda `react-turntable` component never repaints by mutating one
// visible <img>'s `src` — it keeps several absolutely-positioned <img>
// layers (`turntable-img--primary` / `turntable-img--secondary`, etc.)
// and reveals a prepared one instead. Ivanov's Viewer, before this file,
// did the simplest possible thing: one <img>, `.src` reassigned on every
// framechange. That's cheap and has worked, but every reassignment forces
// the *visible, already-painted* element to re-associate its image
// resource in place — there is no separate "prepare, then reveal" step.
//
// This presenter implements the buffered/layered approach with the
// smallest bounded pool that proved the hypothesis in real-browser
// testing: exactly 2 <img> layers, not Nivoda's many. One is always the
// currently-visible ("active") layer; the other is always the hidden
// ("standby") layer a new src is written to. Once that hidden layer
// reports it is actually ready to show (via its own load/error, not a
// guess), the two swap roles — old active -> hidden, prepared standby ->
// visible — and the *next* call reuses whichever layer is now hidden.
// Two was sufficient to reach the approved real-browser result (see
// header above); nothing here pre-renders multiple frames ahead
// simultaneously (that would need a larger pool, and remains explicitly
// out of scope — see the task doc's "DO NOT YET IMPLEMENT 128-POSITION
// SAMPLING").
//
// Contract:
//   createFramePresenter(containerEl, options) -> { show(src), destroy() }
//
//   - containerEl: the element both layers are appended into. Left to the
//     Viewer to size/position (this file adds no layout of its own beyond
//     what CSS already gives `.diamond-viewer__frame` — see
//     component-diamond-viewer.css's absolute-inset layering rules).
//   - options.alt: alt text applied to both layers (only the visible one
//     is ever un-hidden from assistive tech at a time — see _setVisible).
//   - options.createImageElement: element factory, defaults to
//     `document.createElement("img")`. Overridable purely so this file's
//     own Node test harness can exercise the swap/dedupe/cleanup logic
//     with plain mock objects, the same dependency-injection style
//     diamond-frame-engine.js's tests use for `Image` — production code
//     never passes this.
//
//   show(src):
//     - No-op for a falsy src, or a src identical to whatever is already
//       visible (mirrors the exact dedupe guard diamond-viewer.js used to
//       apply itself around `frameImgEl.src = nextSrc` before this file
//       existed — same behavior, just relocated).
//     - Writes src to the current standby layer. If that layer is already
//       immediately usable (`complete && naturalWidth > 0` — typical for
//       prepared-turntable frames already decoded by the Frame Engine),
//       reveal in the same turn and drop the load/error handlers so a
//       later load event cannot reveal twice. Otherwise wait for that
//       layer's own load/error before touching visibility (raw Nivoda /
//       uncached media). If the browser can't resolve that resource on
//       this new element (a corrupt cache entry, an aborted request,
//       complete-but-empty naturalWidth), the presenter simply never
//       reveals it — the previously-visible layer keeps showing, so the
//       stage still never goes blank or shows a broken-image icon.
//     - Calling show() again before a prior swap's load/error has fired
//       retargets that same standby layer (writing the new src cancels
//       the old pending swap outright) rather than racing two swaps
//       against each other — always at most one pending reveal.
//
//   destroy():
//     - Removes both layer elements from the DOM and drops every
//       reference/handler. Idempotent (safe to call twice).
// ————————————————————————————————————————————
(function (global, document) {
  "use strict";

  function noop() {}

  function createFramePresenter(containerEl, options) {
    options = options || {};
    const altText = options.alt || "";
    const createImageElement =
      typeof options.createImageElement === "function"
        ? options.createImageElement
        : () => document.createElement("img");

    const layers = [createImageElement(), createImageElement()];
    layers.forEach((layer) => {
      layer.className = "diamond-viewer__frame";
      if ("draggable" in layer) layer.draggable = false;
      if (typeof layer.setAttribute === "function") {
        layer.setAttribute("alt", altText);
        layer.setAttribute("aria-hidden", "true");
      } else {
        layer.alt = altText;
      }
      if (containerEl && typeof containerEl.appendChild === "function") {
        containerEl.appendChild(layer);
      }
    });

    let activeIndex = -1; // -1: nothing revealed yet (both layers hidden)
    let visibleSrc = null;
    // The src currently being prepared on the standby layer (load/error
    // not yet fired), or null when no swap is in flight. Distinct from
    // visibleSrc so a repeat show() call for the frame *already visible*
    // and a repeat call for the frame *already being prepared* (e.g.
    // diamond-viewer.js's paint() runs on both framechange and a later
    // framestatechange for the same index) are both no-ops, matching the
    // dedupe guard this replaced (`frameImgEl.src === nextSrc`) — neither
    // ever needed to actually re-assign `.src`.
    let pendingSrc = null;
    let destroyed = false;
    // Cleanup for whichever swap is currently pending (load/error not yet
    // fired) on the standby layer — see show()'s "retarget cancels the
    // previous pending swap" note above. Never more than one outstanding.
    let cancelPendingSwap = noop;

    function standbyIndex() {
      return activeIndex === 0 ? 1 : 0;
    }

      function setVisible(index) {
      activeIndex = index;
      layers.forEach((layer, i) => {
        const isVisible = i === index;
        // Hard toggle, on purpose — this presenter implements layered
        // *presentation*, not a new crossfade/motion design. No
        // transition is attached anywhere in the CSS for this class.
        layer.style.opacity = isVisible ? "1" : "0";
        layer.setAttribute("aria-hidden", isVisible ? "false" : "true");
      });
    }

    function show(src) {
      if (destroyed || !src || src === visibleSrc || src === pendingSrc) return;

      cancelPendingSwap();
      cancelPendingSwap = noop;
      pendingSrc = src;

      const targetIndex = activeIndex === -1 ? 0 : standbyIndex();
      const targetLayer = layers[targetIndex];

      function reveal() {
        if (destroyed) return;
        visibleSrc = src;
        pendingSrc = null;
        setVisible(targetIndex);
      }

      let settled = false;
      function onLoad() {
        if (settled) return;
        settled = true;
        cancelPendingSwap = noop;
        reveal();
      }
      function onError() {
        if (settled) return;
        settled = true;
        cancelPendingSwap = noop;
        pendingSrc = null;
        // Never reveal a broken layer — whatever is currently visible
        // (or nothing, on a first-paint failure) simply keeps showing.
      }

      targetLayer.onload = onLoad;
      targetLayer.onerror = onError;
      cancelPendingSwap = () => {
        settled = true;
        targetLayer.onload = null;
        targetLayer.onerror = null;
      };

      targetLayer.src = src;

      // Prepared / memory-cached frames are already browser-decodable
      // after .src assignment. Waiting for a later `load` is the remaining
      // drag-stall source (typically 1–2ms, spikes 8–25ms). Reveal now
      // and neutralize handlers so a queued load/error cannot settle twice.
      // complete && naturalWidth === 0 is not a valid image — leave the
      // existing async error/wait path in place.
      if (targetLayer.complete && targetLayer.naturalWidth > 0) {
        targetLayer.onload = null;
        targetLayer.onerror = null;
        onLoad();
      }
    }

    function destroy() {
      if (destroyed) return;
      destroyed = true;
      cancelPendingSwap();
      cancelPendingSwap = noop;
      pendingSrc = null;
      layers.forEach((layer) => {
        layer.onload = null;
        layer.onerror = null;
        if (layer.parentNode && typeof layer.parentNode.removeChild === "function") {
          layer.parentNode.removeChild(layer);
        }
      });
    }

    return { show, destroy };
  }

  global.DiamondFramePresenter = { createFramePresenter };
})(window, document);
